/**
 * Optimización de imágenes de Cloudinary.
 *
 * Las imágenes de la galería se suben a Cloudinary SIN transformar: la URL
 * original pesa entre 700 KB y 5,6 MB (los SVG trazados de las genéticas son
 * los peores). Servirlas crudo era la causa de que la sección tardara tanto:
 * las 17 cards del carousel + los grupos + el hero sumaban ~68 MB.
 *
 * Estos helpers arman la URL de ENTREGA con transformaciones de Cloudinary
 * (`f_auto` + `q_auto` + ancho máximo) para que el navegador reciba la
 * variante más liviana posible en el formato que soporte (AVIF/WebP/JPEG) y
 * del ancho que realmente necesita la caja donde se muestra.
 * Medido en la cuenta real: 68 MB -> 3,5 MB (-94,8%).
 *
 * Las URLs que NO son de Cloudinary (assets locales, data:, otras CDN) se
 * devuelven intactas, así el helper puede usarse en cualquier `<img>`.
 */

/** `https://res.cloudinary.com/<cloud>/image/upload/` + resto del path. */
const UPLOAD_RE = /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.+)$/i;

/**
 * Devuelve la URL optimizada de una imagen de Cloudinary.
 *
 * @param {string} url  URL original de Cloudinary.
 * @param {object} [options]
 * @param {number} [options.width]   ancho máximo de entrega (px).
 * @param {number} [options.height]  alto máximo de entrega (px).
 * @param {string} [options.crop]    modo de recorte (default `limit`: nunca agranda).
 * @param {string} [options.quality] calidad (default `auto`).
 * @param {string} [options.format]  formato (default `auto` = AVIF/WebP según navegador).
 * @param {number} [options.dpr]     device pixel ratio (opcional).
 * @returns {string} URL optimizada (o la original si no es de Cloudinary).
 */
export function cldOptimized(url, options = {}) {
  if (typeof url !== 'string' || !url) return url;

  const match = UPLOAD_RE.exec(url.trim());
  if (!match) return url;

  const { width, height, crop = 'limit', quality = 'auto', format = 'auto', dpr } = options;

  const parts = [];
  if (format) parts.push(`f_${format}`);
  if (quality) parts.push(`q_${quality}`);
  if (width) parts.push(`w_${width}`);
  if (height) parts.push(`h_${height}`);
  if (crop && (width || height)) parts.push(`c_${crop}`);
  if (dpr) parts.push(`dpr_${dpr}`);
  if (!parts.length) return url;

  // Si la URL ya trae una transformación propia, Cloudinary compone ambas
  // cadenas (`/upload/<propia>/<nuestra>/v123/foto.jpg`), así que se agrega
  // adelante sin tocar la existente.
  return `${match[1]}${parts.join(',')}/${match[2]}`;
}

/**
 * Anchos de entrega por uso dentro de la galería. Un solo lugar para ajustar
 * cuánto pesa cada caja (card del carousel, visor grande, miniatura, IG).
 */
export const IMG = {
  /** Portada de la card del carousel (~250px de ancho, x2 para pantallas retina). */
  card: { width: 520 },
  /** Imagen grande del visor/modal (el panel mide 1000px). */
  stage: { width: 1400 },
  /** Miniatura del visor (72px). */
  thumb: { width: 200 },
  /** Card de posteo de Instagram (~300px, x2). */
  instagram: { width: 640 },
};

export default cldOptimized;
